import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Trophy, Calendar, MapPin, Users, ArrowRight, Flame, CheckCircle2 } from "lucide-react";
import { ddmmyyyyRange } from "../lib/dates";

export default function Events() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/events").then(({ data }) => { setEvents(data); setLoading(false); });
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold uppercase text-white">Events & Tournaments</h1>
                    <p className="text-slate-400 text-sm mt-1">Compete. Win. Take it home.</p>
                </div>
                <span className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66] text-xs font-bold uppercase tracking-widest">
                    <Trophy className="w-4 h-4" /> {events.length} live
                </span>
            </div>

            {loading ? (
                <div className="text-center py-16 text-slate-500 uppercase tracking-widest text-sm">Loading...</div>
            ) : events.length === 0 ? (
                <div className="text-center py-16 text-slate-500">No events yet. Check back soon.</div>
            ) : (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {events.map(e => {
                        const spotsLeft = e.max_teams - (e.registrations_count || 0);
                        return (
                            <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`}
                                className="group bg-slate-900 border border-slate-800 rounded-lg overflow-hidden card-hover">
                                <div className="relative h-44 overflow-hidden">
                                    <img src={e.photo_url || "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1200&q=80"}
                                        alt={e.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/30 to-transparent" />
                                    <div className="absolute top-3 right-3 px-3 h-7 inline-flex items-center rounded-full bg-slate-950/85 border border-[#00FF66]/40 text-[#00FF66] text-[10px] font-bold uppercase tracking-widest">
                                        {e.sport}
                                    </div>
                                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                                        {e.is_completed && (
                                            <span data-testid={`event-completed-badge-${e.id}`}
                                                className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-slate-700/95 text-slate-100 border border-slate-500 text-[10px] font-bold uppercase tracking-widest">
                                                <CheckCircle2 className="w-3 h-3" /> Completed
                                            </span>
                                        )}
                                        {!e.is_completed && e.few_spots_left && (
                                            <span data-testid={`event-fewspots-badge-${e.id}`}
                                                className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-amber-500/95 text-slate-950 border border-amber-300 text-[10px] font-bold uppercase tracking-widest animate-pulse">
                                                <Flame className="w-3 h-3" /> Few spots left
                                            </span>
                                        )}
                                    </div>
                                    <div className="absolute bottom-3 left-4 right-4">
                                        <h3 className="font-display text-xl font-bold uppercase text-white">{e.title}</h3>
                                    </div>
                                </div>
                                <div className="p-4 space-y-2 text-sm">
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <MapPin className="w-3.5 h-3.5 text-[#00FF66]" />
                                        <span className="truncate" data-testid={`event-venue-${e.id}`}>
                                            {e.venue_name || e.centre_name || "TBD"}
                                            {e.centre_city ? `, ${e.centre_city}` : ""}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <Calendar className="w-3.5 h-3.5 text-[#00FF66]" /> {ddmmyyyyRange(e.start_date, e.end_date)}
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <Users className="w-3.5 h-3.5 text-[#00FF66]" /> {e.registrations_count || 0}/{e.max_teams} teams
                                    </div>
                                    <div className="border-t border-slate-800 pt-3 flex items-center justify-between">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Reg. Fee</div>
                                            <div className="font-display text-xl font-bold text-[#00FF66]">₹{e.registration_fee}</div>
                                        </div>
                                        <span className={`text-xs font-bold uppercase tracking-widest ${spotsLeft > 0 ? "text-white" : "text-red-400"}`}>
                                            {spotsLeft > 0 ? `${spotsLeft} spots` : "FULL"}
                                        </span>
                                        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#00FF66]">
                                            View <ArrowRight className="w-3 h-3" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
